import type { SelectField } from '@payloadcms/plugin-form-builder/types'
import type { Control, FieldErrorsImpl } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import {
  Select as SelectComponent,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import React from 'react'
import { Controller } from 'react-hook-form'

import { Error } from '../Error'
import { Width } from '../Width'
import { RadioCard } from '@/components/appraisal/RadioCard'

export const Select: React.FC<
  SelectField & {
    control: Control
    errors: Partial<FieldErrorsImpl>
    usePlaceholders?: boolean
  }
> = ({ name, control, errors, label, options, required, width, defaultValue, usePlaceholders }) => {
  // Mirrors Text/index.tsx's usePlaceholders convention: a visible label above the
  // control (generic in-control placeholder), or a hidden label with the field's own
  // label as the in-control placeholder. Previously always sr-only'd the label AND
  // used the full label text as the trigger placeholder — on Careers' Yes/No question
  // that meant the entire 90-character question rendered inside the trigger with no
  // visible label at all.
  const triggerPlaceholder = usePlaceholders ? `${label}${required ? '*' : ''}` : 'Select an option'

  // Figma renders binary Yes/No selects as a pair of Rich Radios side by side
  // (Careers EOI 4097:112398, "Have you completed a Certificate IV…"), not a dropdown.
  const isYesNo =
    options.length === 2 &&
    options.every((o) => /^(yes|no)$/i.test(o.value) || /^(yes|no)$/i.test(o.label))
  if (isYesNo) {
    return (
      <Width width={width}>
        {/* Figma 4097:112398 sets the Yes/No question in Body/bodyMdAlt sentence case
            (like the section prompts), not the uppercase field label. */}
        <p className="mb-6 text-body-md font-normal text-foreground" id={`${name}-label`}>
          {label}
          {required && ' *'}
        </p>
        <Controller
          control={control}
          defaultValue={defaultValue}
          name={name}
          render={({ field: { onChange, value } }) => (
            <div className="flex gap-2" role="radiogroup" aria-labelledby={`${name}-label`}>
              {options.map((opt) => (
                <RadioCard
                  key={opt.value}
                  label={opt.label}
                  selected={value === opt.value}
                  onSelect={() => onChange(opt.value)}
                  className="flex-1"
                />
              ))}
            </div>
          )}
        />
        {required && errors[name] && <Error name={name} />}
      </Width>
    )
  }

  return (
    <Width width={width}>
      <Label htmlFor={name} className={usePlaceholders ? 'sr-only' : undefined}>
        {label}
        {required && <span> (required)</span>}
      </Label>
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        render={({ field: { onChange, value } }) => {
          const controlledValue = options.find((t) => t.value === value)

          return (
            <SelectComponent onValueChange={(val) => onChange(val)} value={controlledValue?.value}>
              <SelectTrigger className="w-full" id={name}>
                <SelectValue placeholder={triggerPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map(({ label, value }) => {
                  return (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </SelectComponent>
          )
        }}
        rules={{ required }}
      />
      {errors[name] && <Error name={name} />}
    </Width>
  )
}
